import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import BottomSheet from '../BottomSheet';
import { MoneyField, SelectField } from '../FormFields';
import { formatCentsForInput, parseBrazilianMoneyToCents } from '../../domain/finance';
import './styles.css';


const BudgetEditorSheet = ({
  mode,
  monthlyLimitCents = 0,
  categories = [],
  initialCategory = '',
  initialLimitCents = 0,
  canDelete = false,
  onSave,
  onDelete = async () => {},
  onClose,
}) => {
  const isMonthly = mode === 'monthly';
  const [category, setCategory] = useState(initialCategory || categories[0] || '');
  const [value, setValue] = useState(formatCentsForInput(
    isMonthly ? monthlyLimitCents : initialLimitCents
  ));
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const isEditingCategory = !isMonthly && Boolean(initialCategory);

  const title = useMemo(() => {
    if (confirmingDelete) return isMonthly ? 'Excluir orçamento' : 'Remover limite';
    if (isMonthly) return monthlyLimitCents > 0 ? 'Editar orçamento mensal' : 'Criar orçamento mensal';
    return isEditingCategory ? 'Editar limite da categoria' : 'Adicionar limite de categoria';
  }, [confirmingDelete, isEditingCategory, isMonthly, monthlyLimitCents]);

  const handleSave = async () => {
    const limitCents = parseBrazilianMoneyToCents(value);
    if (limitCents === null) {
      setError('Informe um valor válido, sem sinal negativo.');
      return;
    }
    if (!isMonthly && !category) {
      setError('Selecione uma categoria.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await onSave(isMonthly ? { limitCents } : { category, limitCents });
      onClose();
    } catch (saveError) {
      setError('Não foi possível salvar. Confira sua conexão e tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    setError('');
    try {
      await onDelete();
      onClose();
    } catch (deleteError) {
      setError('Não foi possível concluir a remoção. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const footer = confirmingDelete ? (
    <>
      <button type="button" className="nf-sheet__danger" onClick={handleDelete} disabled={submitting}>
        {submitting ? 'Removendo…' : 'Confirmar remoção'}
      </button>
      <button type="button" className="nf-sheet__secondary" onClick={() => setConfirmingDelete(false)} disabled={submitting}>
        Voltar
      </button>
    </>
  ) : (
    <>
      <button type="button" className="nf-sheet__primary" onClick={handleSave} disabled={submitting}>
        {submitting ? 'Salvando…' : 'Salvar limite'}
      </button>
      {canDelete && (
        <button type="button" className="nf-sheet__secondary nf-budget-editor__remove" onClick={() => setConfirmingDelete(true)} disabled={submitting}>
          {isMonthly ? 'Excluir orçamento' : 'Remover limite'}
        </button>
      )}
    </>
  );

  return (
    <BottomSheet
      title={title}
      subtitle={confirmingDelete ? 'Esta ação remove apenas os limites deste mês. Suas movimentações não serão alteradas.' : 'Os gastos são calculados automaticamente a partir das suas movimentações.'}
      onClose={onClose}
      footer={footer}
      size="sm"
      closeOnBackdrop={!submitting}
    >
      {confirmingDelete ? (
        <p className="nf-confirmation-copy">
          {isMonthly
            ? 'O limite geral e todos os limites por categoria deste período serão removidos.'
            : `O limite de ${initialCategory} será removido deste período.`}
        </p>
      ) : (
        <form className="nf-budget-editor" onSubmit={(event) => { event.preventDefault(); handleSave(); }}>
          {!isMonthly && (
            <SelectField
              label="Categoria"
              value={category}
              onChange={setCategory}
              disabled={isEditingCategory || submitting}
              required
            >
              {categories.map((item) => <option value={item} key={item}>{item}</option>)}
            </SelectField>
          )}
          <MoneyField
            label={isMonthly ? 'Limite mensal' : 'Limite da categoria'}
            value={value}
            onChange={setValue}
            error={error}
            placeholder="0,00"
            disabled={submitting}
            required
          />
          <button type="submit" className="nf-budget-editor__hidden-submit" tabIndex="-1" aria-hidden="true">Salvar</button>
        </form>
      )}
      {confirmingDelete && error && <p className="nf-budget-editor__error" role="alert">{error}</p>}
    </BottomSheet>
  );
};

BudgetEditorSheet.propTypes = {
  mode: PropTypes.oneOf(['monthly', 'category']).isRequired,
  monthlyLimitCents: PropTypes.number,
  categories: PropTypes.arrayOf(PropTypes.string),
  initialCategory: PropTypes.string,
  initialLimitCents: PropTypes.number,
  canDelete: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};


export default BudgetEditorSheet;